// VERSION: 2024-01-XX-FIX-TO_LOCALE_STRING-ERROR
// This version fixes the toLocaleString error by using safe formatting functions
// and comprehensive error handling

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Button
} from '@mui/material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Refresh, TrendingUp, TrendingDown } from '@mui/icons-material';
import { DashboardMetrics } from '../types';

interface CEODashboardProps {
  onLoadMetrics: () => Promise<DashboardMetrics>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// COMPLETELY SAFE formatter functions that NEVER use toLocaleString
const safeFormatCurrency = (value: any): string => {
  try {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return '0';
    }
    // Format number WITHOUT toLocaleString
    const rounded = Math.round(num);
    // Simple comma formatting without locale methods
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } catch (e) {
    console.error('Error formatting currency:', e, value);
    return '0';
  }
};

const safeFormatNumber = (value: any): string => {
  try {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return '0';
    }
    // Format number WITHOUT toLocaleString
    const rounded = Math.round(num);
    // Simple comma formatting without locale methods
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } catch (e) {
    console.error('Error formatting number:', e, value);
    return '0';
  }
};

const safeFormatPercent = (value: any): string => {
  try {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.0';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return '0.0';
    }
    return num.toFixed(1);
  } catch (e) {
    console.error('Error formatting percent:', e, value);
    return '0.0';
  }
};

export function CEODashboard({ onLoadMetrics }: CEODashboardProps) {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<Error | null>(null);

  const loadDashboardMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading dashboard metrics...');
      const data = await onLoadMetrics();
      console.log('Dashboard metrics received:', data);
      
      // Ensure data has all required fields with defaults
      const safeData: DashboardMetrics = {
        todayRevenue: data?.todayRevenue || 0,
        weekRevenue: data?.weekRevenue || 0,
        monthRevenue: data?.monthRevenue || 0,
        pendingOrders: data?.pendingOrders || 0,
        channelBreakdown: Array.isArray(data?.channelBreakdown) ? data.channelBreakdown.map(ch => ({
          channel: ch?.channel || 'Unknown',
          orders: ch?.orders || 0,
          revenue: ch?.revenue || 0,
          aov: ch?.aov || 0,
          revenuePercent: ch?.revenuePercent || 0
        })) : [],
        revenueTrend: Array.isArray(data?.revenueTrend) ? data.revenueTrend.map(rt => ({
          date: rt?.date || '',
          revenue: rt?.revenue || 0,
          orders: rt?.orders || 0,
          aov: rt?.aov || 0
        })) : []
      };
      
      console.log('Setting safe metrics:', safeData);
      setMetrics(safeData);
    } catch (err) {
      console.error('Error loading dashboard metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      
      // Set default metrics on error
      const defaultMetrics = {
        todayRevenue: 0,
        weekRevenue: 0,
        monthRevenue: 0,
        pendingOrders: 0,
        channelBreakdown: [],
        revenueTrend: []
      };
      console.log('Setting default metrics due to error:', defaultMetrics);
      setMetrics(defaultMetrics);
    } finally {
      setLoading(false);
    }
  }, [onLoadMetrics]);

  useEffect(() => {
    loadDashboardMetrics();
  }, [loadDashboardMetrics]);

  // Error boundary effect
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught in CEODashboard:', event.error);
      setRenderError(event.error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (renderError) {
    console.error('Render error detected:', renderError);
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">
          An error occurred while rendering the dashboard. Please refresh the page.
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => window.location.reload()} 
          sx={{ mt: 2 }}
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  if (!metrics || typeof metrics !== 'object') {
    console.log('No metrics available, showing loading state. Metrics:', metrics);
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  // Additional safety check - ensure all required properties exist
  const safeMetrics = {
    todayRevenue: metrics?.todayRevenue || 0,
    weekRevenue: metrics?.weekRevenue || 0,
    monthRevenue: metrics?.monthRevenue || 0,
    pendingOrders: metrics?.pendingOrders || 0,
    channelBreakdown: Array.isArray(metrics?.channelBreakdown) ? metrics.channelBreakdown : [],
    revenueTrend: Array.isArray(metrics?.revenueTrend) ? metrics.revenueTrend : []
  };
  
  console.log('Constructed safeMetrics:', safeMetrics);
  
  // Final safety check - ensure all arrays are actually arrays
  if (!Array.isArray(safeMetrics.channelBreakdown)) {
    console.warn('channelBreakdown is not an array, setting to empty array');
    safeMetrics.channelBreakdown = [];
  }
  if (!Array.isArray(safeMetrics.revenueTrend)) {
    console.warn('revenueTrend is not an array, setting to empty array');
    safeMetrics.revenueTrend = [];
  }

  const MetricCard = ({ 
    title, 
    value, 
    change, 
    changeType = 'neutral' 
  }: { 
    title: string; 
    value: string | number; 
    change?: string; 
    changeType?: 'positive' | 'negative' | 'neutral' 
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadDashboardMetrics}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h4" component="div" gutterBottom>
          {value}
        </Typography>
        {change && (
          <Box display="flex" alignItems="center" gap={0.5}>
            {changeType === 'positive' && <TrendingUp color="success" fontSize="small" />}
            {changeType === 'negative' && <TrendingDown color="error" fontSize="small" />}
            <Typography 
              variant="body2" 
              color={changeType === 'positive' ? 'success.main' : changeType === 'negative' ? 'error.main' : 'text.secondary'}
            >
              {change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Safe formatter for chart tooltips
  const formatChartValue = (value: any): string => {
    try {
      if (value === null || value === undefined) return '0';
      const num = Number(value);
      if (isNaN(num)) return '0';
      return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    } catch {
      return '0';
    }
  };

  try {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            CEO Dashboard
          </Typography>
          <Box display="flex" gap={1}>
            {error && (
              <Chip label="Error Loading Data" color="error" size="small" />
            )}
            <Chip label={error ? "Showing Cached Data" : "Live Data"} color={error ? "warning" : "success"} size="small" />
          </Box>
        </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}. Showing cached or default data.
        </Alert>
      )}

      {/* KPI Cards - Today's Revenue removed for debugging toLocaleString error */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
        <MetricCard
          title="Week Revenue"
          value={`$${safeFormatCurrency(safeMetrics.weekRevenue)}`}
          change="+8% vs last week"
          changeType="positive"
        />
        <MetricCard
          title="Month Revenue"
          value={`$${safeFormatCurrency(safeMetrics.monthRevenue)}`}
          change="+15% vs last month"
          changeType="positive"
        />
        <MetricCard
          title="Pending Orders"
          value={safeFormatNumber(safeMetrics.pendingOrders)}
          change="-5% vs yesterday"
          changeType="positive"
        />
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3, mb: 3 }}>
        {/* Revenue Trend Chart */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              30-Day Revenue Trend
            </Typography>
            {safeMetrics.revenueTrend && safeMetrics.revenueTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={safeMetrics.revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip 
                    formatter={(value: any) => [`$${formatChartValue(value)}`, 'Value']}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    name="Revenue"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="orders" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    name="Orders"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="text.secondary">No trend data available</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Channel Performance */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Channel Performance
            </Typography>
            {safeMetrics.channelBreakdown && safeMetrics.channelBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={safeMetrics.channelBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => entry?.channel || 'Unknown'}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {safeMetrics.channelBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value: any) => [`$${formatChartValue(value)}`, 'Revenue']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="text.secondary">No channel data available</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Channel Breakdown Table */}
      {safeMetrics.channelBreakdown && safeMetrics.channelBreakdown.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Channel Performance Details
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Channel
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                      Orders
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                      Revenue
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                      AOV
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {safeMetrics.channelBreakdown.map((channel, index) => (
                    <tr key={`${channel?.channel}-${index}`}>
                      <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                        {channel?.channel || 'Unknown'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                        {safeFormatNumber(channel?.orders)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                        ${safeFormatCurrency(channel?.revenue)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                        ${safeFormatCurrency(channel?.aov)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>
                        {safeFormatPercent(channel?.revenuePercent)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}


    </Box>
    );
  } catch (error) {
    console.error('Error rendering CEO Dashboard:', error);
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">
          An error occurred while rendering the dashboard. Please refresh the page.
        </Alert>
      </Box>
    );
  }
}
